import React from 'react';
import { UIData } from '../../hooks/stores/uiStore';
import { SessionData } from '../../hooks/stores/sessionStore';
import { LauncherWindowData } from '../../hooks/stores/launcherWindowStore';
import { LauncherElementData } from '../../hooks/stores/launcherElementsStore';
import { buildTree } from '../../functions/launcherConfig/buildTree';
import { cleanTree } from '../../functions/launcherConfig/cleanTree';
import { convertConfigToCompatibleFormat } from '../../functions/launcherConfig/convertConfigToCompatibleFormat';
import { flattenTree } from '../../functions/launcherConfig/flattenTree';
import { createThemeVersion } from '../../functions/API';
import { div2ImgDataURL } from '../../functions/utils/div2ImgDataURL';
import { toast } from 'react-toastify';
import configAPI from '../../configAPI.json';

interface ConfigUtilsDependencies {
  UIData: UIData;
  setUIData: (data: Partial<UIData>) => void;
  sessionData: SessionData;
  setSessionData: (data: Partial<SessionData>) => void;
  launcherWindowData: LauncherWindowData;
  setLauncherWindowData: (data: Partial<LauncherWindowData>, replace?: boolean) => void;
  launcherElementsData: Record<string, LauncherElementData>;
  setLauncherElementsData: (data: Partial<Record<string, LauncherElementData>>, replace?: boolean) => void;
  importCanBeDone: boolean;
  setImportCanBeDone: (value: boolean) => void;
  configJSON: string;
  setConfigJSON: (value: string) => void;
}

export function createConfigUtils({
  UIData,
  setUIData,
  sessionData,
  setSessionData,
  launcherWindowData,
  setLauncherWindowData,
  launcherElementsData,
  setLauncherElementsData,
  importCanBeDone,
  setImportCanBeDone,
  configJSON,
  setConfigJSON,
}: ConfigUtilsDependencies) {
  const importConfig = (configJSON: string, quietConversion?: boolean) => {
    const rawConfig = JSON.parse(configJSON);
    const format = rawConfig.formatVersion;

    if (format === '1.0.0') {
      if (quietConversion) {
        setImportCanBeDone(true);
        setConfigJSON(configJSON);
      } else {
        const handleGoToOldEditor = () => {
          window.open(
            configAPI.endpoints.v1EditorPage +
              `?&themeId=${sessionData.themeID}&versionId=${sessionData.versionID}`,
            '_blank'
          );
          setUIData({ isDialogOpen: false });
          setImportCanBeDone(false);
        };

        const handleConvert = () => {
          setUIData({ isDialogOpen: false });
          setImportCanBeDone(true);
          setConfigJSON(configJSON);
        };

        setUIData({
          isDialogOpen: true,
          dialogData: {
            label: 'Deprecated theme format detected',
            children: React.createElement(React.Fragment, null,
              React.createElement('p', { style: { marginTop: 0 } },
                'The theme you are trying to import is created in an old version of the editor. It can be converted, but ',
                React.createElement('b', null,
                  'there is a risk that not everything will work the same way'
                ),
                '.'
              ),
              React.createElement('p', null,
                'Alternatively, you can open the theme in the old editor, but keep in mind that it is no longer supported.'
              )
            ),
            options: [
              {
                text: 'Go to the old editor',
                onClick: handleGoToOldEditor,
              },
              {
                text: 'Convert and import',
                type: 'primary',
                onClick: handleConvert,
              },
            ],
            onClose: () => {
              setUIData({ isDialogOpen: false });
              setImportCanBeDone(false);
            },
          },
        });
      }
    } else {
      setImportCanBeDone(true);
      setConfigJSON(configJSON);
    }
  };

  const executeImportConfig = (configJSON: string) => {
    const config = JSON.parse(convertConfigToCompatibleFormat(configJSON));

    const elementTree = cleanTree(
      [...config.children],
      (node) => node.props.children
    );
    const elementList = flattenTree(
      config.children,
      (node) => node.props.children
    ) as {
      [key: string]: LauncherElementData;
    };

    setLauncherWindowData(
      {
        formatVersion: config.formatVersion,
        windowWidth: config.windowWidth,
        windowHeight: config.windowHeight,
        fonts: config.fonts,
        class: config.class,
        children: elementTree,
      },
      true
    );
    setLauncherElementsData(elementList, true);

    // Set scene zoom to fit launcher window
    const maxWidth = window.innerWidth - (window.innerWidth < 1000 ? 400 : 600);
    const maxHeight = window.innerHeight - 200;
    let zoomWidth = 1;
    let zoomHeight = 1;
    if (config.windowWidth > maxWidth) {
      zoomWidth = 1 / (config.windowWidth / maxWidth);
    }
    if (config.windowHeight > maxHeight) {
      zoomHeight = 1 / (config.windowHeight / maxHeight);
    }
    if (zoomWidth !== 1 || zoomHeight !== 1) {
      const zoom = +Math.min(zoomWidth, zoomHeight).toFixed(1);
      setUIData({ sceneZoom: Math.max(0.2, zoom) });
    }
  };

  const exportConfig = () => {
    const config = {
      formatVersion: '2.0',
      windowWidth: launcherWindowData.windowWidth,
      windowHeight: launcherWindowData.windowHeight,
      fonts: launcherWindowData.fonts,
      class: launcherWindowData.class,
      children: buildTree(launcherElementsData, launcherWindowData.children),
    };

    return JSON.stringify(config);
  };

  const saveThemeVersion = async () => {
    if (!sessionData.themeID) return;

    const currentConfig = exportConfig();
    const thumbnail = await div2ImgDataURL('launcherWindow');

    const newVersion = await toast.promise(
      createThemeVersion({
        themeID: sessionData.themeID,
        themeConfig: currentConfig,
        thumbnail,
      }),
      {
        pending: 'Saving...',
        success: 'Theme saved successfully!',
        error: 'Failed to save theme',
      }
    );

    setSessionData({
      versions: [newVersion, ...(sessionData.versions || [])],
    });
  };

  return {
    importConfig,
    executeImportConfig,
    exportConfig,
    saveThemeVersion,
  };
} 